import { useEffect, useRef, useState } from 'react';
import BrandLogo from '@/components/feature/BrandLogo';
import { brandLogoTone } from '@/lib/placeholders';

interface SplashScreenProps {
  onFinish: () => void;
  isDarkMode: boolean;
}

export default function SplashScreen({ onFinish, isDarkMode }: SplashScreenProps) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    let disposed = false;

    const animateTo = (target: number, duration = 500) => {
      const from = progress;
      const start = performance.now();
      const tick = (now: number) => {
        if (disposed) return;
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        const val = Math.round(from + (target - from) * eased);
        setProgress(val);
        if (p < 1) {
          rafRef.current = requestAnimationFrame(tick);
        }
      };
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    // Progress milestones
    const t1 = setTimeout(() => !disposed && animateTo(18), 250);
    const t2 = setTimeout(() => !disposed && animateTo(38), 700);
    const t3 = setTimeout(() => !disposed && animateTo(55), 1200);
    const t4 = setTimeout(() => !disposed && animateTo(72), 1800);
    const t5 = setTimeout(() => !disposed && animateTo(88), 2400);

    // GUARANTEED finish — always calls onFinish after 3.2s max
    const finishTimer = setTimeout(() => {
      if (!disposed) {
        setProgress(100);
      }
    }, 3000);

    const onFinishTimer = setTimeout(() => {
      if (!disposed) {
        onFinish();
      }
    }, 3300);

    return () => {
      disposed = true;
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      clearTimeout(finishTimer);
      clearTimeout(onFinishTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onFinish]);

  const ringRadius = 80;
  const stroke = 2.5;
  const norm = ringRadius - stroke;
  const circumference = norm * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        background: isDarkMode
          ? 'radial-gradient(ellipse at center, #1a1814 0%, #0f0e0c 100%)'
          : 'radial-gradient(ellipse at center, #faf9f7 0%, #f5f4f2 100%)',
      }}
    >
      {/* Ring + Logo */}
      <div className="relative flex items-center justify-center" style={{ width: ringRadius * 2, height: ringRadius * 2 }}>
        <svg
          width={ringRadius * 2}
          height={ringRadius * 2}
          className="absolute inset-0 rotate-[-90deg]"
        >
          <circle
            stroke={isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}
            strokeWidth={stroke}
            fill="transparent"
            r={norm}
            cx={ringRadius}
            cy={ringRadius}
          />
          <circle
            stroke={isDarkMode ? '#CAA53E' : '#A8892E'}
            strokeWidth={stroke}
            strokeDasharray={`${circumference} ${circumference}`}
            strokeLinecap="round"
            fill="transparent"
            r={norm}
            cx={ringRadius}
            cy={ringRadius}
            style={{
              strokeDashoffset: offset,
              transition: 'stroke-dashoffset 0.35s ease-out',
            }}
          />
        </svg>

        {/* Logo */}
        <div
          className="absolute top-1/2 left-1/2 flex items-center justify-center"
          style={{ transform: 'translate(-50%, -50%)' }}
        >
          <BrandLogo className={`w-12 h-12 ${brandLogoTone(isDarkMode)}`} />
        </div>
      </div>
    </div>
  );
}